import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';
import { soundManager } from '@/lib/sounds';

type HapticIntensity = 'light' | 'medium' | 'heavy';

interface SensoryPreferences {
  hapticsEnabled: boolean;
  soundsEnabled: boolean;
  hapticsIntensity: HapticIntensity;
}

interface SensoryContextType extends SensoryPreferences {
  isLoading: boolean;
  updatePreferences: (preferences: Partial<SensoryPreferences>) => Promise<void>;
  playHaptic: (type: 'light' | 'medium' | 'heavy' | 'success' | 'error' | 'selection') => Promise<void>;
  playSound: (type: 'complete' | 'capture' | 'focus' | 'achievement' | 'break' | 'error' | 'navigation' | 'milestone') => Promise<void>;
}

const SensoryContext = createContext<SensoryContextType | undefined>(undefined);

const isHapticsSupported = Capacitor.isNativePlatform();

export const SensoryProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [preferences, setPreferences] = useState<SensoryPreferences>({
    hapticsEnabled: true,
    soundsEnabled: true,
    hapticsIntensity: 'medium',
  });

  // Fetch preferences from database
  const { data: profile, isLoading } = useQuery({
    queryKey: ['sensoryPreferences', user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('haptics_enabled, sounds_enabled, haptics_intensity')
        .eq('id', user.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  // Update local state when profile data changes
  useEffect(() => {
    if (profile) {
      setPreferences({
        hapticsEnabled: profile.haptics_enabled ?? true,
        soundsEnabled: profile.sounds_enabled ?? true,
        hapticsIntensity: (profile.haptics_intensity as HapticIntensity) ?? 'medium',
      });
      
      // Configure soundManager
      soundManager.setEnabled(profile.sounds_enabled ?? true);
    }
  }, [profile]);

  // Mutation to update preferences
  const updateMutation = useMutation({
    mutationFn: async (newPreferences: Partial<SensoryPreferences>) => {
      if (!user) throw new Error('User not authenticated');

      const updateData: any = {};
      if (newPreferences.hapticsEnabled !== undefined) {
        updateData.haptics_enabled = newPreferences.hapticsEnabled;
      }
      if (newPreferences.soundsEnabled !== undefined) {
        updateData.sounds_enabled = newPreferences.soundsEnabled;
      }
      if (newPreferences.hapticsIntensity !== undefined) {
        updateData.haptics_intensity = newPreferences.hapticsIntensity;
      }

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sensoryPreferences'] });
    },
  });

  const updatePreferences = async (newPreferences: Partial<SensoryPreferences>) => {
    // Update local state immediately for responsiveness
    setPreferences(prev => ({ ...prev, ...newPreferences }));
    
    // Update soundManager if sounds preference changed
    if (newPreferences.soundsEnabled !== undefined) {
      soundManager.setEnabled(newPreferences.soundsEnabled);
    }
    
    // Persist to database
    await updateMutation.mutateAsync(newPreferences);
  };

  const playHaptic = async (type: 'light' | 'medium' | 'heavy' | 'success' | 'error' | 'selection') => {
    if (!preferences.hapticsEnabled || !isHapticsSupported) return;

    try {
      const intensityMap = {
        light: ImpactStyle.Light,
        medium: ImpactStyle.Medium,
        heavy: ImpactStyle.Heavy,
      };

      switch (type) {
        case 'light':
        case 'medium':
        case 'heavy':
          await Haptics.impact({ 
            style: intensityMap[preferences.hapticsIntensity] 
          });
          break;
        case 'success':
          await Haptics.notification({ type: NotificationType.Success });
          break;
        case 'error':
          await Haptics.notification({ type: NotificationType.Error });
          break;
        case 'selection':
          await Haptics.selectionStart();
          await Haptics.selectionChanged();
          await Haptics.selectionEnd();
          break;
      }
    } catch (error) {
      console.log('[Haptics] Not available or error:', error);
    }
  };

  const playSound = async (type: 'complete' | 'capture' | 'focus' | 'achievement' | 'break' | 'error' | 'navigation' | 'milestone') => {
    if (!preferences.soundsEnabled) return;

    switch (type) {
      case 'complete':
        await soundManager.playTaskComplete();
        break;
      case 'capture':
        await soundManager.playCapture();
        break;
      case 'focus':
        await soundManager.playFocusStart();
        break;
      case 'achievement':
        await soundManager.playAchievement();
        break;
      case 'break':
        await soundManager.playBreak();
        break;
      case 'error':
        await soundManager.playError();
        break;
      case 'navigation':
        await soundManager.playNavigation();
        break;
      case 'milestone':
        await soundManager.playMilestone();
        break;
    }
  };

  return (
    <SensoryContext.Provider
      value={{
        ...preferences,
        isLoading,
        updatePreferences,
        playHaptic,
        playSound,
      }}
    >
      {children}
    </SensoryContext.Provider>
  );
};

export const useSensory = () => {
  const context = useContext(SensoryContext);
  if (context === undefined) {
    throw new Error('useSensory must be used within a SensoryProvider');
  }
  return context;
};
